import { useQuery, UseQueryOptions } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { FulfilmentClient } from '@jbhi-fi/apis/src/fulfilment';
import { FulfilmentOptionsLineItem, FulfilmentOptionsResponse } from '@jbhi-fi/apis/src/fulfilment/types';
import { useRecaptchaContext } from '../../context/RecaptchaProvider';
import { FULFILMENT_API_RECAPTCHA_ACTION } from '@jbhi-fi/apis/src/fulfilment/FulfilmentClient';
import { CountryCode } from '@jbhi-fi/apis/src/utils/multiTenancyHeaders';

export type UseFulfilmentOptionsResult = {
  data?: Array<FulfilmentOptionsResponse>;
  isLoading: boolean;
  error?: unknown;
};

type Address = { countryCode: CountryCode; postcode: string; suburb: string };

type UseFulfilmentOptionsProps = {
  fulfilmentClient?: FulfilmentClient;
  isEnabled?: boolean;
  addresses: Array<Address | null>;
  lineItems: Array<FulfilmentOptionsLineItem>;
  requiresScheduledOptions?: boolean;
} & Pick<UseQueryOptions, 'cacheTime' | 'staleTime'>;

const getAreAllAddressesIdentical = (addresses: Array<Address | null>) => {
  if (addresses.length < 2 || addresses.some(a => a === null)) return false;

  // Have already returned false if any addresses are nulls
  const nonNullAddresses = addresses as Array<Address>;

  const firstAddress = nonNullAddresses[0];
  return nonNullAddresses.every(
    address =>
      firstAddress.countryCode === address.countryCode &&
      firstAddress.postcode === address.postcode &&
      firstAddress.suburb === address.suburb
  );
};

export const useFulfilmentOptions = ({
  fulfilmentClient: fulfilmentClientProp,
  addresses,
  lineItems,
  isEnabled,
  requiresScheduledOptions = true,
  ...useQueryOptions
}: UseFulfilmentOptionsProps): UseFulfilmentOptionsResult => {
  const { fulfilmentClient: fulfilmentClientContext } = usePublicApiClient();
  const fulfilmentClient = fulfilmentClientProp || fulfilmentClientContext;
  const { generateToken, isReady: isRecaptchaReady } = useRecaptchaContext();

  if (!fulfilmentClient) {
    throw new Error('useFulfilmentOptions must be used with a contentfulThemeApiClient');
  }

  const areAllAddressesIdentical = getAreAllAddressesIdentical(addresses);
  const consolidatedAddresses = areAllAddressesIdentical ? [addresses[0]] : addresses;

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'fulfilmentOptions',
      consolidatedAddresses.map(address =>
        address
          ? {
              countryCode: address?.countryCode,
              postcode: address?.postcode,
              suburb: address?.suburb,
            }
          : null
      ),
      lineItems.map(lineItem => ({
        sku: lineItem.SKU ?? '',
        quantity: lineItem.Quantity ?? 1,
        lineItemFreightPrice: lineItem.LineItemFreightPrice ?? 0,
        giftCardType: lineItem.GiftCardType ?? null,
        tieredIds: lineItem.TieredIds,
      })),
      { requiresScheduledOptions },
    ],
    queryFn: async () => {
      const recaptchaToken = await generateToken?.(FULFILMENT_API_RECAPTCHA_ACTION);

      const response = await fulfilmentClient?.getMultipleOptions(
        consolidatedAddresses.map(address =>
          address
            ? {
                Address: {
                  CountryCode: address?.countryCode ?? 'AU',
                  Postcode: address?.postcode ?? '',
                  Suburb: address?.suburb ?? '',
                },
                LineItems: lineItems,
                requiresScheduledOptions,
              }
            : null
        ),
        recaptchaToken ?? ''
      );
      if (response.error || !response?.data) {
        throw `Failed to fetch availability: ${response?.error?.message} ${response?.statusCode}`;
      }

      return response;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    enabled: isEnabled && isRecaptchaReady && !!lineItems?.length && addresses.some(a => !!a?.postcode),
    ...useQueryOptions,
  });

  const reconstitutedData =
    areAllAddressesIdentical && data?.data ? Array(addresses.length).fill(data?.data[0]) : data?.data;

  return { isLoading: isLoading || !isRecaptchaReady, data: reconstitutedData, error };
};
